import {
  AttributionCodeSchema,
  UuidV4Schema,
  VerifyPrivacyRequestSchema,
} from '@devmil/contracts';
import {useId, useState, type FormEvent} from 'react';

import {usePanel} from '../../app/PanelProvider.js';
import {useReauth} from '../../auth/ReauthContext.js';
import {describeProblem, type MissingProof} from '../../lib/panel-problem.js';

/**
 * Prova do titular.
 *
 * O código digitado aqui é o que o TITULAR mandou de volta pelo canal dele. Ele
 * segue a mesma regra do código de resgate: vive no estado do formulário e no
 * corpo do POST, some no sucesso, e nunca encosta em URL nem em armazenamento.
 *
 * O `challengeId` e o `evidenceEventId` vêm do desafio já emitido. A tela pede
 * os dois em vez de inventá-los porque quem os emite é a API, e um identificador
 * fabricado aqui produziria uma verificação que não aponta para prova nenhuma.
 */
export const PrivacyVerificationPanel = ({
  requestId,
  onVerified,
}: {
  requestId: string;
  onVerified: () => void;
}) => {
  const {dataSource} = usePanel();
  const reauth = useReauth();
  const challengeInputId = useId();
  const evidenceInputId = useId();
  const codeInputId = useId();
  const headingId = useId();

  const [challengeId, setChallengeId] = useState('');
  const [evidenceEventId, setEvidenceEventId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [missingProof, setMissingProof] = useState<MissingProof | null>(null);

  // Separado do handler de submit para o diálogo de reauth poder repetir a
  // verificação depois do passkey confirmado, validação incluída.
  const tentarVerificacao = () => {
    if (busy) {
      return;
    }

    const corpo = VerifyPrivacyRequestSchema.safeParse({
      challengeId,
      verificationCode,
      evidenceEventId,
    });
    if (!corpo.success) {
      setMissingProof(null);
      setProblem(
        UuidV4Schema.safeParse(challengeId).success &&
          UuidV4Schema.safeParse(evidenceEventId).success
          ? 'O código do titular tem 22 caracteres, sem espaço nem acento.'
          : 'Desafio e evidência são identificadores completos, no formato que a API emitiu.',
      );
      return;
    }

    setBusy(true);
    setProblem(null);
    setMissingProof(null);
    void dataSource
      .verifyPrivacyRequest(requestId, corpo.data)
      .then(() => {
        setChallengeId('');
        setEvidenceEventId('');
        setVerificationCode('');
        onVerified();
      })
      .catch((erro: unknown) => {
        // Sem contexto de titular aqui de propósito: este formulário É a prova
        // do titular, então uma precondição pendente só pode ser o passkey.
        const descrito = describeProblem(erro, {subjectVerified: true});
        setProblem(descrito.message);
        setMissingProof(descrito.missingProof);
        if (descrito.missingProof === 'passkey_reauth' && reauth.armado) {
          reauth.oferecer({
            message: descrito.message,
            retry: tentarVerificacao,
          });
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    // Antes de tudo: envio nativo viraria GET com o código na query string.
    event.preventDefault();
    tentarVerificacao();
  };

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Verificar o titular</h2>
        <span className="dm-illustrative">CANAL DO TITULAR</span>
      </div>
      <p className="dm-subtitle">
        Sua sessão no painel não prova nada sobre o titular. Só o retorno pelo
        canal dele libera a execução do direito.
      </p>

      <form className="dm-form" onSubmit={submit} noValidate>
        <div className="dm-field">
          <label htmlFor={challengeInputId}>Identificador do desafio</label>
          <input
            id={challengeInputId}
            type="text"
            value={challengeId}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setChallengeId(event.target.value);
            }}
          />
        </div>

        <div className="dm-field">
          <label htmlFor={evidenceInputId}>Identificador da evidência</label>
          <input
            id={evidenceInputId}
            type="text"
            value={evidenceEventId}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setEvidenceEventId(event.target.value);
            }}
          />
        </div>

        <div className="dm-field">
          <label htmlFor={codeInputId}>Código de verificação do titular</label>
          <input
            id={codeInputId}
            type="text"
            value={verificationCode}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            maxLength={22}
            onChange={(event) => {
              setVerificationCode(event.target.value);
            }}
          />
          <small>
            {AttributionCodeSchema.safeParse(verificationCode).success
              ? 'Formato aceito.'
              : '22 caracteres, exatamente como o titular respondeu.'}
          </small>
        </div>

        <div className="dm-actions">
          <button
            className="dm-action dm-action-primary"
            type="submit"
            disabled={busy}
          >
            Registrar prova do titular
          </button>
        </div>
      </form>

      {problem ? (
        <div className="dm-alert" role="alert">
          {problem}
          {missingProof === 'passkey_reauth'
            ? ' Confirme o passkey e repita esta verificação.'
            : null}
        </div>
      ) : null}
    </section>
  );
};
